// Libs
import classNames from 'classnames/bind';
import { ChangeEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  TableContainer,
  Paper,
  Box,
} from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
// Components, Layouts, Pages
import { BaseInput, BasePagination } from '~/components/common';
import { OrderItemDetailModal } from '~/components/module/order';
// Others
import { IGroupDay } from '~/utils/interface/inventory';
import { IFormOrder } from '~/utils/interface/order';
import { IQuoteListItem } from '~/utils/interface/quotes';
import { EMPTY_STRING, REG_EXP } from '~/utils/constants/common';
import { formatCurrencyUSD, formatDate } from '~/utils/helper/common';
import { DateFormatEnum, OrderStatusEnum, TypeInventoryGroupDay, TypeListInventory } from '~/utils/enum/common';
// Styles, images, icons
import styles from './TableOrderItem.module.scss';
import { icons } from '~/assets';

type Props = {
  totalPage?: number;
  currentPage?: number;
  onPageChange?: (event: ChangeEvent<unknown>, page: number) => void;
};

const cx = classNames.bind(styles);

const TableOrderItem = (props: Props) => {
  //#region Destructuring Props
  const { totalPage, currentPage, onPageChange } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/order', 'common/base']);
  const {
    control,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<IFormOrder>();

  const {
    startDate,
    endDate,
    listOrderItems,
    selectIdProductGroup,
    selectIdVariantGroup,
    selectQuantityProduct,
    deleteProduct,
    deleteChildrenProduct = [],
    orderItems,
    status,
    orderAddOns,
    deleteItemIds,
  } = watch();
  //#endregion Declare Hook

  //#region Declare State
  const [isModalOrderItemDetail, setIsModalOrderItemDetail] = useState<boolean>(false);
  const [daySelected, setDaySelected] = useState<IGroupDay>();
  const [inventorySelected, setInventorySelected] = useState<IQuoteListItem>();
  const [typeSelected, setTypeSelected] = useState<string>();
  //#endregion Declare State

  //#region Implement Hook
  useEffect(() => {
    if (!selectIdProductGroup) return;

    const productIndex =
      listOrderItems?.findIndex(
        (item) => item.id === selectIdProductGroup && (item.variantId || EMPTY_STRING) === selectIdVariantGroup
      ) || 0;

    setValue(`listOrderItems.${productIndex}.quantity`, String(selectQuantityProduct));
  }, [selectQuantityProduct, deleteProduct]);

  useEffect(() => {
    if (!deleteChildrenProduct) return;

    deleteChildrenProduct?.forEach((id) => {
      const index = listOrderItems?.findIndex((item) => item.id === id);
      if (index !== undefined && index !== -1) {
        setValue(`listOrderItems.${index}.quantity`, '0');
      }
    });
  }, [JSON.stringify(deleteChildrenProduct)]);

  useEffect(() => {
    if (!startDate || !endDate || !listOrderItems) {
      setValue('isOverbooking', false);
      return;
    }

    const isOverbooking = listOrderItems.some((item) => {
      const sumQuantity = handleSumQuantity(item.id, item?.variantId!);
      const isValid = item?.mergedDays?.every((day) => {
        const maxInventory =
          day.type === TypeInventoryGroupDay.ORDER || day.type === TypeInventoryGroupDay.ALL
            ? Number(day.availableInventory)
            : Number(item.quantityInventory);

        return sumQuantity <= maxInventory || item.type === TypeListInventory.PACKAGE;
      });

      return sumQuantity > 0 && !isValid;
    });

    setValue('isOverbooking', isOverbooking, { shouldValidate: true });
  }, [startDate, endDate, JSON.stringify(listOrderItems), JSON.stringify(orderItems), JSON.stringify(orderAddOns)]);

  const isItemValid = (item: any) => {
    const sumQuantity = handleSumQuantity(item.id, item.variantId!);

    if (!item?.mergedDays?.length) return true;

    return item.mergedDays.some((day: any) => {
      const maxInventory =
        day.type === TypeInventoryGroupDay.ORDER || day.type === TypeInventoryGroupDay.ALL
          ? Number(day.availableInventory)
          : Number(item.quantityInventory);

      return sumQuantity <= maxInventory || item.type === TypeListInventory.PACKAGE;
    });
  };
  //#endregion Implement Hook

  //#region Handle Function
  const handleShowModalOrderItemDetail = () => {
    setIsModalOrderItemDetail(!isModalOrderItemDetail);
  };

  const handleClickItem = (day: IGroupDay, inventorySelected: IQuoteListItem, type: string) => {
    setDaySelected(day);
    setTypeSelected(type);
    setInventorySelected(inventorySelected);
    handleShowModalOrderItemDetail();
  };

  const handleSumQuantity = (inventoryId: string, variantId: string) => {
    return [...(orderItems ?? []), ...(orderAddOns ?? [])]
      .filter((item) => item?.inventoryId === inventoryId && item?.variantId === variantId)
      .reduce((sum, item) => sum + Number(item.quantity), 0);
  };

  const handleChangeQuantity = (item: IQuoteListItem, quantity: number) => {
    setValue(`selectIdProductOrder`, item.id);
    setValue('selectIdVariantOrder', item.variantId || EMPTY_STRING);
    setValue('selectGroupItem', EMPTY_STRING);
    setValue('selectIdProductGroup', EMPTY_STRING);
    setValue('selectIdVariantGroup', EMPTY_STRING);

    if (quantity === 0) {
      const dataFind = [...(orderItems || []), ...(orderAddOns || [])].find(
        (data) => data.inventoryId === item.id && (data.variantId || EMPTY_STRING) === (item.variantId || EMPTY_STRING)
      );

      if (dataFind?.idDetail) {
        if (!deleteItemIds?.includes(String(dataFind.idDetail))) {
          setValue('deleteItemIds', [...(deleteItemIds || []), String(dataFind.idDetail)]);
        }
      }
    }
  };

  const renderDayIcons = (day: IGroupDay, item: IQuoteListItem) =>
    item.type === TypeListInventory.PACKAGE || !day.type || day.type === TypeInventoryGroupDay.DEFAULT ? null : (
      <Stack direction='row'>
        {(day.type === TypeInventoryGroupDay.ALL
          ? [TypeInventoryGroupDay.QUOTE, TypeInventoryGroupDay.ORDER]
          : [day.type]
        ).map((type) => (
          <IconButton key={type} onClick={() => handleClickItem(day, item, type)}>
            <Box className={cx('viewBoxType')}>
              <Typography className={cx('textType')}>{type === TypeInventoryGroupDay.ORDER ? 'O' : 'Q'}</Typography>
            </Box>
          </IconButton>
        ))}
      </Stack>
    );
  //#endregion Handle Function

  return (
    <Stack id={'tableOrderItemComponent'} className={cx('container')}>
      <TableContainer component={Paper} className={cx('containerTable')}>
        <Table>
          <TableHead className={cx('tableHead')}>
            <TableRow>
              <TableCell width={'22%'} className={cx('titleHead')}>
                {t('form_orders_table_title_item')}
              </TableCell>

              <TableCell width={'20%'} className={cx('titleHead')}>
                {t('form_orders_table_title_price')}
              </TableCell>

              <TableCell width={'15%'} className={cx('titleHead')} align='center'>
                {t('form_orders_table_title_qty')}
              </TableCell>

              {startDate &&
                endDate &&
                listOrderItems &&
                listOrderItems[0]?.mergedDays?.map((day, dayIndex) => (
                  <TableCell width={'12%'} align='center' key={dayIndex} className={cx('titleHead')}>
                    {formatDate(day.day, DateFormatEnum.MMM_D)}
                  </TableCell>
                ))}
            </TableRow>
          </TableHead>

          <Stack height={'20px'} />

          <TableBody>
            {listOrderItems &&
              listOrderItems?.length > 0 &&
              listOrderItems?.map((item, itemIndex) => {
                const fieldName = `listOrderItems.${itemIndex}` as const;
                const isValid = isItemValid(item);

                let rowClass = 'rowDefault';
                let cellClass = 'cellDefault';

                if (startDate && endDate) {
                  rowClass = Number(item.quantity) === 0 ? 'rowDefault' : isValid ? 'rowValid' : 'rowInvalid';
                  cellClass = Number(item.quantity) === 0 ? 'cellDefault' : isValid ? 'cellValid' : 'cellInvalid';
                }

                return (
                  <TableRow key={itemIndex} className={cx(rowClass)}>
                    <TableCell width={'22%'} className={cx('textCell', cellClass)}>
                      <Stack direction={'row'} gap={2} alignItems={'center'}>
                        <Stack direction={'column'} gap={1}>
                          <Box>{item.name}</Box>

                          {item?.variantName && <Box className={cx('textVariant')}>{item.variantName}</Box>}
                        </Stack>

                        {item.type === TypeListInventory.PACKAGE && (
                          <Box className={cx('viewBox')}>{t('form_orders_label_package')}</Box>
                        )}
                      </Stack>

                      {item.note && (
                        <Typography className={cx('textNote')}>{`${t('form_orders_table_title_note_value')} ${
                          item.note
                        }`}</Typography>
                      )}
                    </TableCell>

                    <TableCell width={'20%'} className={cx('textCell', cellClass)}>
                      {formatCurrencyUSD({ value: item.rentalPrice, showDecimal: true })}
                    </TableCell>

                    <TableCell width={'15%'} align='center' className={cx('textCell', cellClass)}>
                      <Controller
                        name={`${fieldName}.quantity`}
                        control={control}
                        defaultValue={item.quantity}
                        render={({ field: { onChange, value } }) => (
                          <Stack
                            direction='row'
                            spacing={1}
                            alignItems='center'
                            justifyItems={'center'}
                            gap={2}
                            className={cx('viewQuantity')}
                          >
                            <IconButton
                              className={cx('buttonQty', { disabled: status === OrderStatusEnum.FINALIZED })}
                              disabled={status === OrderStatusEnum.FINALIZED}
                              onClick={() => {
                                const newQty = Math.max(Number(value) - 1, 0);
                                setValue(`${fieldName}.quantity`, newQty.toString());
                                handleChangeQuantity(item, newQty);
                              }}
                            >
                              <img src={icons.commonIconMinus} alt={t('common_alt_img')} />
                            </IconButton>

                            <BaseInput
                              height={31}
                              textAlignCenter
                              width={55}
                              value={value}
                              borderRadius={'4px'}
                              disabled={status === OrderStatusEnum.FINALIZED}
                              onChange={(e) => {
                                const inputValue = e.target.value.replace(REG_EXP.NUMBER, EMPTY_STRING);
                                const numericValue = inputValue ? Number(inputValue) : EMPTY_STRING;
                                onChange(numericValue);
                                handleChangeQuantity(item, Number(numericValue));
                              }}
                            />

                            <IconButton
                              className={cx('buttonQty', { disabled: status === OrderStatusEnum.FINALIZED })}
                              disabled={status === OrderStatusEnum.FINALIZED}
                              onClick={() => {
                                const newQty = Number(value) + 1;
                                setValue(`${fieldName}.quantity`, newQty.toString());
                                handleChangeQuantity(item, newQty);
                              }}
                            >
                              <img src={icons.commonIconPlusGrey} alt={t('common_alt_img')} />
                            </IconButton>
                          </Stack>
                        )}
                      />
                    </TableCell>

                    {startDate &&
                      endDate &&
                      item.mergedDays?.map((day, dayIndex) => (
                        <TableCell
                          width={'12%'}
                          align='center'
                          className={cx('textCell', cellClass, isItemValid(item) ? 'textValid' : 'textInvalid')}
                          key={dayIndex}
                        >
                          <Stack direction={'row'} alignItems={'center'} justifyContent={'center'} gap={1}>
                            {(() => {
                              const quantity = handleSumQuantity(item.id, item.variantId!);

                              const maxInventory =
                                day.type === TypeInventoryGroupDay.ORDER || day.type === TypeInventoryGroupDay.ALL
                                  ? Number(day.availableInventory)
                                  : Number(item.quantityInventory);

                              if (item.type === TypeListInventory.PACKAGE) {
                                return `${quantity}`;
                              }

                              return `${quantity}/${maxInventory}`;
                            })()}
                            {renderDayIcons(day, item)}
                          </Stack>
                        </TableCell>
                      ))}
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>

      {listOrderItems && listOrderItems?.length > 0 && (
        <BasePagination count={totalPage} page={currentPage} onChange={onPageChange} />
      )}

      {isModalOrderItemDetail && (
        <OrderItemDetailModal
          onClose={handleShowModalOrderItemDetail}
          dataInventory={inventorySelected}
          day={daySelected}
          type={typeSelected}
        />
      )}
    </Stack>
  );
};

export default TableOrderItem;
